import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { Select } from 'antd'
import { omit } from 'lodash'
import moment, { Moment } from 'moment'

import { Label } from '@/components/molecules/label'
import { Button } from '@/components/atoms'
import { DateField } from '@/components/molecules/date-field'
import { InputField } from '@/components/molecules/input-field'
import { EmployeeType, OperationLogLegacyFiltersType } from '@/models/operation-log'
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { OPERATION_LOG_FILTER_FEATURES, TABLES_LIST } from '@/constants/operation-log'
import { normalizeHalfHourSelectTime } from '@/utils/operation-log-select-time'
import { getHotelEmployeeList } from '@/apis/aipass'
import { errorHandler } from '@/libs/errors'

type OperationLogLegacySearchProps = {
  onChange: (data: OperationLogLegacyFiltersType) => void
  initialFilters?: OperationLogLegacyFiltersType
}

type LegacyFiltersInputType = Omit<OperationLogLegacyFiltersType, 'beginDate' | 'endDate' | 'staff'> & {
  beginDate?: Moment
  beginTime?: string
  endDate?: Moment
  endTime?: string
  staff?: number | string
}

function parseInitialMoment(iso?: string): Moment | undefined {
  if (iso == null || String(iso).trim() === '') return undefined
  const m = moment(iso)
  return m.isValid() ? m : undefined
}

function parseTimeFromIso(iso: string | undefined, fallback: string | undefined): string | undefined {
  if (iso == null || !iso.includes(' ')) return normalizeHalfHourSelectTime(fallback) ?? fallback
  const part = iso.split(' ')[1]
  if (part == null) return normalizeHalfHourSelectTime(fallback) ?? fallback
  const normalized = normalizeHalfHourSelectTime(part)
  return normalized || fallback
}

/**
 * 旧操作ログの詳細検索と同一構成:
 * 左: 操作日時（日付〜日付＋時刻）/ スタッフ / アカウント
 * 右: 予約ID / 機能 / 対象データ
 */
export const OperationLogLegacySearch: React.FC<OperationLogLegacySearchProps> = ({ onChange, initialFilters }) => {
  const { t, i18n } = useTranslation()
  const { employeeStaff } = useContext<AccountContextValue>(AccountContext)

  const defaultValues: Partial<LegacyFiltersInputType> = initialFilters
    ? {
        beginDate: parseInitialMoment(initialFilters.beginDate),
        endDate: parseInitialMoment(initialFilters.endDate),
        beginTime: parseTimeFromIso(initialFilters.beginDate, undefined),
        endTime: parseTimeFromIso(initialFilters.endDate, undefined),
        staff: initialFilters.staff,
        employee: initialFilters.employee,
        reservationId: initialFilters.reservationId,
        features: initialFilters.features,
        table: initialFilters.table,
      }
    : {}

  const { control, handleSubmit, reset } = useForm<LegacyFiltersInputType>({
    defaultValues,
  })
  const [employeeList, setEmployeeList] = useState<EmployeeType[]>([])
  const [times] = useState([
    { name: '指定なし', value: '' },
    ...[...Array(48)].map((_, i) => {
      const hour = Math.floor(i / 2)
      const minute = i % 2 === 0 ? '00' : '30'
      const hourStr = String(hour).padStart(2, '0')
      return { value: `${hourStr}:${minute}`, name: `${hourStr}:${minute}` }
    }),
  ])

  const onClear = () => {
    reset({
      beginDate: undefined,
      endDate: undefined,
      beginTime: undefined,
      endTime: undefined,
      staff: undefined,
      employee: undefined,
      reservationId: undefined,
      features: undefined,
      table: undefined,
    })
  }

  const onSearch = (data: LegacyFiltersInputType) => {
    const beginDateStr = data.beginDate
      ? moment(data.beginDate).format('YYYY-MM-DD') + (data.beginTime != null && data.beginTime !== '' ? ` ${data.beginTime}:00` : '')
      : undefined
    const endDateStr = data.endDate
      ? moment(data.endDate).format('YYYY-MM-DD') + (data.endTime != null && data.endTime !== '' ? ` ${data.endTime}:00` : ' 23:59')
      : undefined

    const staffNum = data.staff != null && data.staff !== '' ? Number(data.staff) : undefined
    const reservationIdTrimmed = data.reservationId?.trim()

    onChange({
      ...omit(data, ['beginDate', 'beginTime', 'endDate', 'endTime', 'staff', 'reservationId']),
      beginDate: beginDateStr !== '' ? beginDateStr : undefined,
      endDate: endDateStr !== '' ? endDateStr : undefined,
      staff: staffNum !== undefined && !Number.isNaN(staffNum) ? staffNum : undefined,
      reservationId: reservationIdTrimmed !== '' ? reservationIdTrimmed : undefined,
    })
  }

  const fetchHotelEmployeeList = async () => {
    try {
      setEmployeeList(await getHotelEmployeeList())
    } catch (error) {
      errorHandler(error)
    }
  }

  const getLogTranslation = (key: string) => {
    const trimmed = (key ?? '').toString().trim()
    if (!trimmed) return '-'
    if (i18n.exists(`OperationLog.itemLabel.${trimmed}`)) return t(`OperationLog.itemLabel.${trimmed}`)
    if (i18n.exists(`OperationLog.${trimmed}`)) return t(`OperationLog.${trimmed}`)
    return t(trimmed)
  }

  useEffect(() => {
    fetchHotelEmployeeList()
  }, [])

  return (
    <div css={dropDownResearchPanelStyle}>
      <div css={{ display: 'flex', margin: 24 }}>
        <div css={{ flex: 1 }}>
          <div css={{ margin: 8 }}>
            <Label label={t('Date and time of operation')} />
            <div css={{ display: 'flex', alignItems: 'center', margin: '0 -8px' }}>
              <Controller
                control={control}
                name="beginDate"
                render={({ field: { onChange: oc, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <DateField value={value} onChange={oc} placeholder={t('Date')} />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="beginTime"
                render={({ field: { onChange: oc, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <Select
                      css={selectBoxStyle}
                      suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                      value={value}
                      onChange={oc}
                    >
                      {times.map((item, index) => (
                        <Select.Option key={index} value={item.value}>
                          {item.name === '指定なし' ? '-' : item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}
              />
              <div css={{ margin: '0 8px' }}>~</div>
              <Controller
                control={control}
                name="endDate"
                render={({ field: { onChange: oc, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <DateField value={value} onChange={oc} placeholder={t('Date')} />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="endTime"
                render={({ field: { onChange: oc, value } }) => (
                  <div css={{ margin: '0 8px' }}>
                    <Select
                      css={selectBoxStyle}
                      suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                      value={value}
                      onChange={oc}
                    >
                      {times.map((item, index) => (
                        <Select.Option key={index} value={item.value}>
                          {item.name === '指定なし' ? '-' : item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}
              />
            </div>
          </div>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              control={control}
              name="staff"
              render={({ field: { onChange: oc, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Staff')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={oc}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {employeeStaff?.map((staff, index) => (
                      <Select.Option key={staff.id ?? `staff-${index}`} value={staff.id}>
                        {staff.name ?? '-'}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
            <Controller
              control={control}
              name="employee"
              render={({ field: { onChange: oc, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Account')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={oc}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {employeeList.map(v => (
                      <Select.Option key={v.id} value={v.id}>
                        {v.name || v.email}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
          </div>
        </div>

        <div css={{ flex: 1 }}>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              control={control}
              name="reservationId"
              render={({ field: { onChange: oc, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <InputField value={value ?? ''} handleChangeData={oc} label={t('Reservation ID')} marginBottom={0} width="100%" />
                </div>
              )}
            />
            <div css={{ flex: 1, margin: 8 }} />
          </div>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              control={control}
              name="features"
              render={({ field: { onChange: oc, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Features')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={oc}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {OPERATION_LOG_FILTER_FEATURES.map(v => (
                      <Select.Option key={v.value} value={v.value}>
                        {t(v.i18nKey)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
            <Controller
              control={control}
              name="table"
              render={({ field: { onChange: oc, value } }) => (
                <div css={{ flex: 1, margin: 8 }}>
                  <Label label={t('Table')} />
                  <Select
                    css={[selectBoxStyle, { width: '100%' }]}
                    suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
                    value={value}
                    onChange={oc}
                  >
                    <Select.Option value={undefined}>-</Select.Option>
                    {TABLES_LIST.map(v => (
                      <Select.Option key={v} value={v}>
                        {getLogTranslation(v)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <div css={bottomResearchPanelStyle}>
        <div css={assignButtonContainerStyle}>
          <Button width={92} height={32} buttonType={3} fontSize={12} onClick={onClear}>
            {t('Clear')}
          </Button>
          <Button width={93} height={32} buttonType={1} fontSize={12} marginLeft={16} onClick={handleSubmit(onSearch)}>
            {t('Search')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const dropDownResearchPanelStyle = css({
  width: '100%',
  zIndex: 50,
  backgroundColor: '#FFF',
  position: 'absolute',
  top: 50,
  left: 0,
  boxShadow: '0px 3px 10px #0000003d',
})

const bottomResearchPanelStyle = css({
  borderTop: '1px solid rgb(242, 242, 242)',
  display: 'flex',
  padding: '16px 32px',
})

const assignButtonContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})

const selectBoxStyle = css({
  width: 92,
  '> div': {
    borderRadius: '16px',
    border: '1px solid #CCCCCC',
    boxShadow: 'none !important',
    borderColor: '#CCCCCC !important',
  },
})
